/**
 * React Query hooks for library sections
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLibrarySections,
  createLibrarySection,
  updateLibrarySection,
  deleteLibrarySection,
} from '@/api/libraries';
import { LibrarySection, CreateLibrarySectionRequest, UpdateLibrarySectionRequest } from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch library sections
 */
export const useLibrarySections = (libraryId: number) => {
  return useQuery<LibrarySection[], Error>({
    queryKey: ['libraries', libraryId, 'sections'],
    queryFn: () => getLibrarySections(libraryId),
    enabled: !!libraryId,
  });
};

/**
 * Hook to create library section
 */
export const useCreateLibrarySection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LibrarySection,
    Error,
    { libraryId: number; data: CreateLibrarySectionRequest }
  >({
    mutationFn: ({ libraryId, data }) => createLibrarySection(libraryId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'sections'],
      });
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'stats'],
      });
    },
    onError: (error) => {
      console.error('Failed to create section:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to delete library section
 */
export const useDeleteLibrarySection = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { libraryId: number; sectionId: number }>({
    mutationFn: ({ libraryId, sectionId }) => deleteLibrarySection(libraryId, sectionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'sections'],
      });
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'stats'],
      });
    },
    onError: (error) => {
      console.error('Failed to delete section:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to update library section
 */
export const useUpdateLibrarySection = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LibrarySection,
    Error,
    { libraryId: number; sectionId: number; data: UpdateLibrarySectionRequest }
  >({
    mutationFn: ({ libraryId, sectionId, data }) =>
      updateLibrarySection(libraryId, sectionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'sections'],
      });
    },
    onError: (error) => {
      console.error('Failed to update section:', getErrorMessage(error));
    },
  });
};
